
// 切り替えマスタ取得
// 指定したタグを切替先タグへ変換する
import { ApiBaseFront } from '../api-base-front';
import { TagConversionsRequest, TagConversionsResponse } from '../../../models/api/front/tag-conversions';
import { AppThunk, RootState } from '../../../app/store';
import { apiActions } from '../../../slices/api-slice';
import { ResponseBase } from '../../response-base';
import { ordersActions } from '../../../slices/orders-slice';

export class ApiTagConversionsPost extends ApiBaseFront<TagConversionsResponse> {
  constructor(param: TagConversionsRequest) {
    super(
      'POST',
      '/api/v1/tag-conversions/bulk',
      'JSON',
      param,
    );
  }
}

const findToTagId = (res: ResponseBase<TagConversionsResponse>, fromTagId: string) => {
  if (Number(res?.error?.errorCode) !== 200) {
    return '';
  }
  return res?.body?.data?.conversions?.find((v) => v.fromTagId === fromTagId)?.toTagId || '';
};

type LaminateEntity = { laminateId: string, laminateName: string };

type LaminateConversionTarget = {
  // 対象部位の現在の選択値(応答待ちの間に選択が変わっていないかの確認に使う)
  getValue: (state: RootState) => string | undefined,
  // 対象部位の現在のラミ一覧(切替先の存在チェックに使う)
  getList: (state: RootState) => LaminateEntity[],
  // 切替先への置き換え
  set: (laminate: LaminateEntity) => AppThunk,
  // 変換できない場合に未選択へ戻す処理(従来動作を維持する部位は undefined)
  clear?: () => AppThunk,
};

/**
 * ラミ切替の共通処理
 * 現在の一覧に存在しないラミIDを切り替えマスタで変換し、切替先が一覧に存在すれば置き換える。
 * 変換できない場合は target.clear があれば未選択に戻し、無ければ何もしない。
 * apiName は同名の後発リクエストに onSuccess を無効化されるため部位ごとに分ける
 */
const apiTagConversionLaminateThunk = (
  kijshopCd: string,
  fromTagId: string,
  apiName: string,
  target: LaminateConversionTarget,
): AppThunk => async (dispatch, getState) => {
  // 応答待ちの間に選択が変わっていたら何もしない
  const isCurrent = () => target.getValue(getState()) === fromTagId;
  const clear = () => {
    if (isCurrent() && target.clear) {
      dispatch(target.clear());
    }
  };
  dispatch(apiActions.run(
    new ApiTagConversionsPost({ kijshopCd, fromTagId: [fromTagId] }),
    {
      onSuccess: (res: ResponseBase<TagConversionsResponse>) => {
        if (!isCurrent()) {
          return;
        }
        const toTagId = findToTagId(res, fromTagId);
        const laminate = toTagId ? target.getList(getState()).find((v) => v.laminateId === toTagId) : undefined;
        if (laminate) {
          dispatch(target.set(laminate));
        } else {
          clear();
        }
      },
      onError: clear,
    },
    { apiName, ignoreSystemError: true },
  ));
};

/**
 * 表紙ラミの切替
 * clearable(新規作成時) の場合のみ、変換できなければ従来通り未選択に戻す
 * 注文編集で開いた既存注文は既存の選択値を維持する
 */
export const apiTagConversionCoverLaminateThunk = (kijshopCd: string, fromTagId: string, clearable: boolean = true): AppThunk =>
  apiTagConversionLaminateThunk(kijshopCd, fromTagId, 'tag-conversions-cover', {
    getValue: (state) => state.order.cover.laminate.value,
    getList: (state) => state.orderData.cover.laminate.laminate,
    set: (laminate) => ordersActions.cover.setLaminate(laminate.laminateId, laminate.laminateName),
    clear: clearable ? () => ordersActions.cover.setLaminate('') : undefined,
  });

/**
 * 本身ラミの切替
 * clearable(新規作成時) の場合のみ、変換できなければ従来通り未選択に戻す
 * 注文編集で開いた既存注文は既存の選択値を維持する
 */
export const apiTagConversionGenuineLaminateThunk = (kijshopCd: string, fromTagId: string, clearable: boolean = true): AppThunk =>
  apiTagConversionLaminateThunk(kijshopCd, fromTagId, 'tag-conversions-genuine', {
    getValue: (state) => state.order.genuine.laminate.value,
    getList: (state) => state.orderData.genuine.laminate.laminate,
    set: (laminate) => ordersActions.genuine.setLaminate(laminate.laminateId, laminate.laminateName),
    clear: clearable ? () => ordersActions.genuine.setLaminate('') : undefined,
  });

/**
 * オプション表紙ラミの切替
 * 変換できない場合は何もしない(従来動作を維持)
 */
export const apiTagConversionOptionLaminateThunk = (kijshopCd: string, fromTagId: string, index: number): AppThunk =>
  // オプションは複数 index が同時に走り得るため apiName は index 込みで分ける
  apiTagConversionLaminateThunk(kijshopCd, fromTagId, `tag-conversions-option-${index}`, {
    getValue: (state) => state.order.option[index]?.cover?.laminate?.value,
    getList: (state) => state.orderData.option.optionLaminate[index]?.laminate || [],
    set: (laminate) => ordersActions.option.setLaminate(laminate.laminateId, index, laminate.laminateName),
  });
